import type React from 'react';
import { type MonthlyAdherence, type WeeklyAdherence } from '../../types/goals';

interface AdherenceChartProps {
  weeklyAdherence?: WeeklyAdherence;
  monthlyAdherence?: MonthlyAdherence;
  type: 'weekly' | 'monthly';
  className?: string;
}

const getColorClass = (value: number): string => {
  if (value >= 80) {
    return 'bg-green-500';
  }
  if (value >= 60) {
    return 'bg-blue-500';
  }
  if (value >= 40) {
    return 'bg-yellow-500';
  }
  return 'bg-red-500';
};

export const AdherenceChart: React.FC<AdherenceChartProps> = ({
  weeklyAdherence,
  monthlyAdherence,
  type,
  className = ''
}) => {
  if (type === 'weekly' && weeklyAdherence) {
    return <WeeklyChart adherence={weeklyAdherence} className={className} />;
  }

  if (type === 'monthly' && monthlyAdherence) {
    return <MonthlyChart adherence={monthlyAdherence} className={className} />;
  }

  return null;
};

interface WeeklyChartProps {
  adherence: WeeklyAdherence;
  className?: string;
}

const WeeklyChart: React.FC<WeeklyChartProps> = ({ adherence, className = '' }) => {
  const days = [
    { key: 'sun', label: 'Sun', value: adherence.sun },
    { key: 'mon', label: 'Mon', value: adherence.mon },
    { key: 'tue', label: 'Tue', value: adherence.tue },
    { key: 'wed', label: 'Wed', value: adherence.wed },
    { key: 'thu', label: 'Thu', value: adherence.thu },
    { key: 'fri', label: 'Fri', value: adherence.fri },
    { key: 'sat', label: 'Sat', value: adherence.sat }
  ];

  const maxValue = Math.max(...days.map(day => day.value));

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Weekly Progress</h3>
      <p className="text-sm text-gray-600 mb-6">{adherence.description}</p>
      
      <div className="flex items-end justify-between gap-2 h-32">
        {days.map((day) => {
          const heightPercentage = maxValue > 0 ? (day.value / maxValue) * 100 : 0;
          const colorClass = getColorClass(day.value);

          return (
            <div key={day.key} className="flex flex-col items-center flex-1">
              <div className="relative w-full flex items-end justify-center h-24">
                <div
                  className={`w-full rounded-t transition-all duration-300 ${colorClass}`}
                  style={{ height: `${heightPercentage}%` }}
                  title={`${day.label}: ${day.value}%`}
                />
              </div>
              <div className="mt-2 text-center">
                <div className="text-xs font-medium text-gray-700">{day.label}</div>
                <div className="text-xs text-gray-500">{day.value}%</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface MonthlyChartProps {
  adherence: MonthlyAdherence;
  className?: string;
}

const MonthlyChart: React.FC<MonthlyChartProps> = ({ adherence, className = '' }) => {
  const daysInMonth = adherence.progress.length;

  // Create grid layout for calendar view
  const weeksArray: number[][] = [];
  let currentWeek: number[] = [];
  
  adherence.progress.forEach((value, index) => {
    currentWeek.push(value);
    if (currentWeek.length === 7 || index === daysInMonth - 1) {
      weeksArray.push([...currentWeek]);
      currentWeek = [];
    }
  });

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Progress</h3>
      <p className="text-sm text-gray-600 mb-6">{adherence.description}</p>
      
      <div className="space-y-1">
        {weeksArray.map((week, weekIndex) => (
          <div key={`week-${weekIndex}`} className="flex gap-1">
            {week.map((value, dayIndex) => {
              const actualDayIndex = (weekIndex * 7) + dayIndex;
              if (actualDayIndex >= daysInMonth) {
                return null;
              }

              const colorClass = value === 0 ? 'bg-gray-200' : getColorClass(value);

              return (
                <div
                  key={`day-${actualDayIndex}`}
                  className={`w-4 h-4 rounded-sm ${colorClass}`}
                  title={`Day ${actualDayIndex + 1}: ${value}%`}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <span>Less</span>
        <div className="flex gap-1">
          <div className="w-3 h-3 bg-gray-200 rounded-sm" />
          <div className="w-3 h-3 bg-red-500 rounded-sm" />
          <div className="w-3 h-3 bg-yellow-500 rounded-sm" />
          <div className="w-3 h-3 bg-blue-500 rounded-sm" />
          <div className="w-3 h-3 bg-green-500 rounded-sm" />
        </div>
        <span>More</span>
      </div>
    </div>
  );
};